import axios from "axios";
import request, { RequestOptionConfig } from "../request";
import { getReadyFailedHook, getReadySuccessHook, regReadyHook } from "../js-api-tools";
import corsImport from "../corsImport";
import mockTEAMS from "./mockTEAMS";
import setTheme from "../theme";

function noop () { }

export interface WeaverSDKConfig {
  /* 是否启用 ecode */
  enableEcode?: boolean;
  /* OA 系统域名地址 */
  oadomain?: 'string';
  /* OA 租户id */
  oatenantkey?: 'string';
  /* OA eteamsid */
  eteamsid?: 'string';
  /* 获取认证 token 的 api 接口 */
  authTokenApi?: 'string';
  /* 获取认证 token 的 请求钩子 */
  authTokenRequest?: () => Promise<{ status: boolean, data: { token: string } }>;
  /* api 中转接口 */
  transferApi?: 'string';
  /* api 接口前缀 */
  prefixApiPath?: 'string';
  /* api 接口二级域名 */
  apiDomain?: 'string';
  /* 静态资源二级域名 */
  staticDomain?: 'string';
  /* 跨域接口开启 withCredentials */
  withCredentials?: boolean;
  /* ReactRouter 路由模式 */
  routerType?: 'BrowserRouter' | 'MemoryRouter' | 'HashRouter';
  /* Router props */
  routerProps?: Object;
  /** 主题样式配置 */
  theme?: any;
};

/**
 * 外部三方系统调用 E10 组件 SDK
 */
class WeaverSDK {
  // 避免 init 重复调用
  _initedCalled = false;

  // 全局标识 - 是否在三方系统
  outer = false;
  // 初始化配置
  config: WeaverSDKConfig = {};
  // 是否初始化
  inited = false;
  // 缓存 TEAMS 对象
  TEAMSCatch = null;
  // 认证 token 数据
  authToken = '';
  // 认证 eteamsid 数据
  authEteamsid = '';
  // 接口请求拦截
  requestInterceptors: Function[] = [];
  // 接口返回拦截
  responseInterceptors: Function[] = [];
  // 已注册主题
  themeConfig: Record<string, any> = {};

  /**
   * 初始化
   * @param {*} config 
   */
  init(config: WeaverSDKConfig) {
    /* 无需重复执行 init 方法 */
    if (this._initedCalled) {
      return new Promise((resolve, reject) => {
        this.ready(resolve);
        this.error(reject);
      });
    }
    
    this._initedCalled = true;
    // 1、设置全局标识
    this.setGlobalSign();
    // 2、更新配置
    Object.assign(this.config, config || {});
    let chain: Promise<any> = Promise.resolve();
    chain = chain.then(() => {
      if (this.config.authTokenApi || this.config.authTokenRequest) {
        // 3、请求认证 token
        return this.authByApi();
      } else if (!this.isEteamsidExist()) {
        // 3、获取 eteamsid
        if (this.config.eteamsid) {
          document.cookie = `ETEAMSID=${this.config.eteamsid}`;
          return Promise.resolve({});
        } else if (this.config.oadomain && this.config.oatenantkey) {
          return this.authByScript();
        }
      }
    });
    chain = chain.then(() => {
      return new Promise<any>((resolve, reject) => {
        // 4、api 接口重写
        this.apiOverwrite();
        // 5、加载资源
        const loadPromise = this.loadResource();
        // 6、获取 TEAMS 对象
        const fetchTeams = this.fetchTeams();
        Promise.allSettled([
          loadPromise, fetchTeams,
        ]).then(([res1, res2]) => {
          if (
            res1.status === 'fulfilled' &&
            res2.status === 'fulfilled'
          ) {
            resolve({});
          }
        });
      })
    });
    return chain.then((data) => {
      const { theme } = this.config;
      // 更新主题样式
      const themeOptions = typeof theme === 'string' ? this.themeConfig[theme] : theme;
      themeOptions && setTheme(false, themeOptions);
      // 其他初始化处理...
      this.inited = true;
      const success = getReadySuccessHook('weappUtils', 'WeaverSDKInitReady');
      if (typeof success === 'function') {
        success(data);
      }
    }).catch(err => {
      console?.error(err);
      const failed = getReadyFailedHook('weappUtils', 'WeaverSDKInitReady');
      if (typeof failed === 'function') {
        failed(err);
      }
    });
  }

  /**
   * 判断 cookie 中是否存在 eteamsid
   * @returns 
   */
  isEteamsidExist () {
    const index = document?.cookie?.indexOf?.('ETEAMSID');
    if (index !== -1) {
      var end = document.cookie.indexOf(';', index);
      this.authEteamsid = document.cookie.slice(index + 9, end === -1 ? undefined : end);   
      return true;
    }
    return false;
  }

  /**
   * script 标签方式获取 eteamsid
   * @returns 
   */
  authByScript () {
    const { oadomain, oatenantkey } = this.config;
    // 通过 script 方式调用集成认证的接口，获取 cookie 信息
    return new Promise((resolve, reject) => {
      var gopage = encodeURI(`${oadomain}/404`);
      var script = document.createElement('script');
      script.src = `${oadomain}/papi/iaauthclient/oauth/login?tk=${oatenantkey}&gopage=${gopage}`;
      document.documentElement.appendChild(script);
      let count = 0;
      const cookieChecker = setInterval(() => {
        count ++;
        if (this.isEteamsidExist()) {
          clearInterval(cookieChecker);
          resolve({});
        } else if (count > 100) {
          clearInterval(cookieChecker);
          reject(new Error('无法获取到 ETEAMSID ！'));
        }
      }, 20);
    });
  }

  /**
   * 认证接口方式获取 token
   */
  authByApi () {
    const { authTokenApi, authTokenRequest } = this.config;
    let promise: Promise<any>;
    if (authTokenApi) {
      promise = request({ url: authTokenApi, method: 'get' });
    } else if (authTokenRequest) {
      promise = authTokenRequest();
      if (!(promise?.then && typeof promise?.then === 'function')) {
        return Promise.reject(new Error('获取 Token 失败！'));
      }
    } else {
     promise = Promise.reject(new Error('获取 Token 失败！'));
    }
    return new Promise((resolve, reject) => {
      // 3、请求接口获取 token
      promise.then((res: any) => {
        const { data } = res;
        if (res.status && res.data) {
          const { token, eteamsid } = data;
          this.authToken = token;
          if (eteamsid) {
            // token 接口直接返回 eteamsid
            this.authEteamsid = eteamsid;
            resolve(res);
          } else {
            request({
              url: `${this.config.apiDomain || ''}/papi/proxy/weaver/transfer`,
              method: 'POST',
              headers: { token: this.authToken },
              data: { token: this.authToken },
            }).then(res => {
              if (res.status && res.data) {
                const { eteamsid } = res.data;
                this.authEteamsid = eteamsid;
                resolve(res);
              } else {
                reject(new Error(res.msg));
              }
            }).catch((err: any) => {
              console?.error?.(err);
              reject(err);
            });
          }
        } else {
          reject(new Error(res.msg));
        }
      }).catch((err: any) => {
        console?.error?.(err);
        reject(err);
      });
    });
  }

  /**
   * 接口重写
   * @param config 
   */
  apiOverwrite () {
    const { transferApi, prefixApiPath, apiDomain, staticDomain } = this.config;
    // 1、api 中转接口
    // 2、token/eteamsid 认证参数
    // 3、特殊接口请求拦截处理
    // 如果指定里 api 中转接口，需要拦截 E10 的请求并走中转接口处理
    axios.interceptors.request.use((config: RequestOptionConfig) => {
      let newConfig = config;
      const oldUrl = newConfig.url;
      // 拦截接口请求
      if (this.requestInterceptors.length) {
        this.requestInterceptors.forEach(rI => {
          if (typeof rI === 'function') {
            try {
              newConfig = rI(newConfig) || newConfig;
            } catch (e) {
              console.error?.(e);
            }
          }
        });
      }
      if (transferApi && oldUrl === newConfig.url) {
        if (config.url && config.cacheRequestKey) {
          const newData = {
            route: config.url,
            method: config.method || 'GET',
            form: config.params,
            body: config.data,
            contentType: config.headers?.contentType || 'application/json;charset=utf-8',
          };
          newConfig.url = transferApi;
          newConfig.method = 'POST';
          newConfig.data = newData;
        }
      }
      if (this.authEteamsid) {
        newConfig.headers = {
          eteamsid: this.authEteamsid,
          ...newConfig.headers,
        };
      }
      return newConfig;
    });
    // 4、特殊接口请求返回结果拦截处理
    axios.interceptors.response.use((res) => {
      let newRes = res;
      // 拦截接口返回
      if (this.responseInterceptors.length) {
        this.responseInterceptors.forEach(rI => {
          if (typeof rI === 'function') {
            try {
              newRes = rI(res) || newRes;
            } catch (e) {
              console.error?.(e);
            }
          }
        });
      }
      return newRes;
    });
    // 处理 api 多级路径
    if (prefixApiPath) {
      window.publicUrlapi = prefixApiPath;
    }
    // 处理 api 接口二级域名
    if (apiDomain) {
      window.publicDomainapi = apiDomain;
    }
    // 处理静态资源二级域名
    if (staticDomain) {
      window.publicDomainstatic = staticDomain;
    }
  }

  /**
   * init 加载必要资源
   */
  loadResource () {
    if (this.config.enableEcode) {
      return corsImport('@weapp/ecodesdk').then(res => {
        if (res?.loadAsyncReady) {
          return res.loadAsyncReady().finally(() => {
            return corsImport('@weapp/ui', '', true);
          });
        } else {
          return corsImport('@weapp/ui', '', true);
        }
      });
    } else {
      return corsImport('@weapp/ui', '', true);
    }
  }

  /**
   * init 登录认证
   */
  fetchTeams () {
    return new Promise((resolve, reject) => {
      // 1、如果 SDK 上已经记录了 TEAMS 对象，直接读取无需请求
      if (this.TEAMSCatch) {
        resolve(window.TEAMS);
        return;
      }
      // 2、如果 sessionStorage 中缓存了，直接读取无需请求
      let hasTeams = false;
      const teamsStrCache = sessionStorage.getItem('weaver_teams_obj_cache');
      if (teamsStrCache) {
        try {
          const teamsObj = JSON.parse(teamsStrCache);
          this.TEAMSCatch = teamsObj;
          window.TEAMS = teamsObj;
          hasTeams = true;
        } catch (err) {
          console.error(err);
        }
      }
      if (hasTeams) {
        resolve(window.TEAMS);
        return;
      }
      // 3、如果没有任何缓存，请求接口获取
      request({
        url: '/api/baseserver/layout/baseTeams',
        method: 'POST',
      }).then((res: any) => {
        const { data: teamsObj } = res;
        if (res.status && res.data) {
          try {
            const teamsStr = JSON.stringify(teamsObj);
            sessionStorage.setItem('weaver_teams_obj_cache', teamsStr);
            this.TEAMSCatch = teamsObj;
            window.TEAMS = teamsObj;
            resolve(window.TEAMS);
          } catch (err) {
            console?.error(err);
            reject(err);
          }
        } else {
          reject(new Error(res.msg));
        }
      }).catch((err: any) => {
        console.error(err);
        reject(err);
      });
    }).then(() => {
      // 给 TEAMS 对象上添加函数
      mockTEAMS();
    });
  }

  /**
   * init 执行成功 ready 回调
   * @param fn 
   * @returns 
   */
  ready(fn: Function) {
    if (typeof fn !== 'function') return;
    regReadyHook('weappUtils', 'WeaverSDKInitReady', {
      success: (data) => fn(data),
      failed: noop,
    });
  }

  /**
   * init 执行失败 error 回调
   * @param fn 
   * @returns 
   */
  error(fn: Function) {
    if (typeof fn !== 'function') return;
    regReadyHook('weappUtils', 'WeaverSDKInitReady', {
      success: noop,
      failed: (err) => fn(err),
    });
  }

  /**
   * 设置全局标识
   */
  setGlobalSign() {
    this.outer = true;
  }

  /**
   * 获取全局标识
   */
  getGlobalSign() {
    return this.outer;
  }

  /**
   * 是否在三方系统
   * @returns 
   */
  isExternSystem() {
    return this.getGlobalSign();
  }

  /**
   * 获取 OA 域名地址
   * @returns 
   */
  getOADomain() {
    return this.config.oadomain || window.location.origin;
  }

  requestOverwrite (fn: Function) {
    if (typeof fn === 'function') {
      this.requestInterceptors.push(fn);
    }
    return () => {
      this.requestInterceptors = this.requestInterceptors.filter(f => f !== fn);
    };
  }

  unRequestOverwrite (fn: Function) {
    this.requestInterceptors = this.requestInterceptors.filter(f => f !== fn);
  }

  responseOverwrite (fn: Function) {
    if (typeof fn === 'function') {
      this.responseInterceptors.push(fn);
    }
    return () => {
      this.responseInterceptors = this.responseInterceptors.filter(f => f !== fn);
    };
  }

  unResponseOverwrite (fn: Function) {
    this.responseInterceptors = this.responseInterceptors.filter(f => f !== fn);
  }

  /**
   * 渲染组件
   */
  compRender(opt: any) {
    const { routerType: globalRouterType, routerProps: globalRouterProps } = this.config || {};
    const { container, compName, params, routerType: rType, routerProps: rProps } = opt || {};
    return new Promise((resolve, reject) => {
      if (!this.inited) {
        reject(new Error('weaverSDK 为初始化，请调用 weaverSDK.init 初始化！'))
        return;
      }
      if (!container || !compName) {
        reject(new Error(`传入参数缺少 container 和 compName！`))
        return;
      }
      const { Suspense, createElement: createEle } = window.React;
      const render = window.ReactDOM.render;
      const [app, cName] = compName.split('.');
      let content = null;
      if (app === 'ui') {
        content = createEle(window.weappUi[cName], params);
      } else if (app === 'utils') {
        content = createEle(window.weappUtils[cName], params);
      } else {
        content = createEle(Suspense, { fallback: null },
          createEle(window.weappUi.CorsComponent, {
            app: `@weapp/${app}`,
            compName: cName,
            ...params || {},
          })
        );
      }
      const routerType = rType || globalRouterType || "BrowserRouter";
      const routerProps = { ...globalRouterProps, ...rProps };
      if (routerType === 'MemoryRouter') {
        const Router = window.ReactRouterDOM.MemoryRouter;
        content = createEle(Router, routerProps, content);
      } else if (routerType === 'HashRouter') {
        const Router = window.ReactRouterDOM.HashRouter;
        content = createEle(Router, routerProps, content);
      } else {
        const Router = window.ReactRouterDOM.BrowserRouter;
        content = createEle(Router, routerProps, content);
      }
      render(content, container);
      resolve({});
    });
  }

  /**
   * 方法调用
   * @param {*} opt 
   */
  funcApply(opt: any) {
    const {funcName, params } = opt || {};
    return new Promise((resolve, reject) => {
      if (!this.inited) {
        reject(new Error('weaverSDK 为初始化，请调用 weaverSDK.init 初始化！'))
        return;
      }
      if (!funcName) {
        reject(new Error(`传入参数缺少 container 和 funcName`))
        return;
      }
      const [app, fName] = funcName.split('.');
      if (app === 'ui') {
        const func = (window.weappUi[fName]) || false;
        if (typeof func === 'function') {
          resolve(func(params));
        } else {
          reject(new Error(`${funcName} 函数不存在！`));
        }
      } else if (app === 'utils') {
        const func = (window.weappUtils[fName]) || false;
        if (typeof func === 'function') {
          resolve(func(params));
        } else {
          reject(new Error(`${funcName} 函数不存在！`));
        }
      } else {
        corsImport(`@weapp/${app}`)
          .then((module: any) => {
            const func = (module && module[fName]) || false;
            if (typeof func === 'function') {
              resolve(func(params));
            } else {
              reject(new Error(`${funcName} 函数不存在！`));
            }
          })
          .catch((err: any) => {
            console?.error(err);
            reject(err);
          });
      }
    });
  }

  registerTheme(name: string, theme: Record<string, string | number>) {
    if (typeof name !== 'string') {
      return ;
    }
    this.themeConfig[name] = theme;
  }
}

window.weaverSDK = window.weaverSDK || new WeaverSDK();
